'use client';

import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { CarouselProgress } from '@eliadmin/core/src/components/ui/carousel-progress';
import type { StoreReview } from '@eliadmin/core/src/api/general/general-api';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FaStar } from 'react-icons/fa';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { IoCheckmarkCircle } from 'react-icons/io5';

export function Reviews({
  reviewsTitle,
  reviews,
}: {
  reviewsTitle: string;
  reviews: StoreReview[];
}) {
  const options = {
    align: 'center' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section className={cn('custom-section', 'px-0', 'generic-gap', 'section-padding-l-center')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start px-4 md:flex-row md:items-center md:justify-between xl:px-0',
          'generic-gap',
        )}
      >
        <Heading title={reviewsTitle} />
      </div>
      <div className="relative w-full">
        <div
          className={cn(
            'absolute right-0 top-0 z-10 hidden h-full w-[164px] md:block',
            'ea-reviews__right-overlay',
          )}
        />
        <CarouselControls
          className={{
            main: cn(
              'custom-container',
              'absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 items-center justify-between sm:flex',
            ),
            button: cn(
              'max-h-12 min-h-12 min-w-12 max-w-12 rounded-full border bg-white !p-0 text-black',
              'ea-reviews__carousel-controls-button',
            ),
          }}
          buttonProps={{ variant: 'white' }}
          prevIcon={<FiArrowLeft size={20} className="text-[#111827]" />}
          nextIcon={<FiArrowRight size={20} className="text-[#111827]" />}
        />
        <Carousel
          options={options}
          className={{
            wrapper: 'w-full',
            container: 'w-full gap-3 sm:gap-4 lg:gap-6',
            main: 'w-full',
            slide: 'w-full max-w-[320px] sm:max-w-[380px] md:max-w-[460px] lg:max-w-[518px]',
          }}
          slides={reviews.map(review => (
            <ReviewCard review={review} key={review.id} />
          ))}
        />
        <div
          className={cn(
            'absolute left-0 top-0 z-10 hidden h-full w-[164px] -scale-[1] md:block',
            'ea-reviews__right-overlay',
          )}
        />
      </div>
      <div className="flex w-full max-w-[450px] items-center justify-center px-4">
        <CarouselProgress
          selectedIndex={selectedIndex}
          totalSlides={reviews.length}
          className={{ activeBar: 'h-[2px] bg-[#111827]', container: 'w-full bg-[#E5E7EB]' }}
        />
      </div>
    </section>
  );
}

function ReviewCard({ review }: { review: StoreReview }) {
  const { t } = useTranslations();
  const filledStars = Math.round(Math.min(5, Math.max(0, parseFloat(review.rating) || 0)));

  return (
    <div className="flex h-[349px] w-full flex-col items-start justify-between gap-4 rounded-[20px] bg-[#F9FAFB] p-6">
      <div className="flex w-full flex-col items-start justify-start gap-3">
        <div className="flex items-center justify-start gap-3">
          <div className="flex items-center justify-start gap-[6px]">
            {Array.from({ length: 5 }).map((_, index) => {
              return (
                <FaStar
                  key={index}
                  size={20}
                  className={index < filledStars ? 'text-[#EDB000]' : 'text-[#E5E7EB]'}
                />
              );
            })}
          </div>
          <div className="h-5 w-[1px] bg-border" />
          <div className="flex items-center justify-start gap-1">
            <IoCheckmarkCircle size={20} className="text-[#008000]" />
            <Paragraph size="sm" className="text-[#008000]">
              {t('text3685')}
            </Paragraph>
          </div>
        </div>
        <Paragraph
          size="base"
          color="black"
          className="line-clamp-6 w-full"
          innerHtml={review.review}
        />
      </div>
      <Paragraph size="sm">
        <strong className="font-semibold">{review.name}</strong>
      </Paragraph>
    </div>
  );
}
